import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode, Ref } from 'react';
import styles from './primitives.module.css';

/**
 * TESSMA ONE frozen primitives.
 *
 * These are transcriptions of the approved design, not new design work. What
 * this layer adds over the approved prototype is accessibility: every control
 * is a real button or link with a name, a role and keyboard operation, which
 * the Phase 1 audit records as designed but not implemented (R-08).
 */

/* ------------------------------------------------------------------ */
/* Button                                                              */
/* ------------------------------------------------------------------ */

export type ButtonVariant =
  'primary' | 'secondary' | 'tertiary' | 'destructive' | 'ai' | 'aiOutlined';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  /** Header scale: 38px tall with an 8px radius, per §10. */
  readonly headerScale?: boolean;
  readonly fullWidth?: boolean;
  /** Leading icon. Decorative — the label carries the accessible name. */
  readonly icon?: ReactNode;
  readonly ref?: Ref<HTMLButtonElement>;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: styles.primary ?? '',
  secondary: styles.secondary ?? '',
  tertiary: styles.tertiary ?? '',
  destructive: styles.destructive ?? '',
  ai: styles.ai ?? '',
  aiOutlined: styles.aiOutlined ?? '',
};

export function Button({
  variant = 'primary',
  headerScale = false,
  fullWidth = false,
  icon,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    styles.control,
    VARIANT_CLASS[variant],
    headerScale ? styles.headerScale : '',
    fullWidth ? styles.fullWidth : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type={type} className={classes} {...rest}>
      {icon !== undefined && <span aria-hidden="true">{icon}</span>}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* IconButton — always carries an accessible name (closes R-08)        */
/* ------------------------------------------------------------------ */

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required. An icon-only control with no name is unusable to a screen reader. */
  readonly label: string;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly bare?: boolean;
  readonly ref?: Ref<HTMLButtonElement>;
}

const ICON_SIZE_CLASS = { sm: styles.iconSm, md: styles.iconMd, lg: styles.iconLg } as const;

export function IconButton({
  label,
  size = 'md',
  bare = false,
  children,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const classes = [
    styles.control,
    styles.iconButton,
    ICON_SIZE_CLASS[size],
    bare ? styles.iconBare : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type={type} className={classes} aria-label={label} title={label} {...rest}>
      <span aria-hidden="true">{children}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* StatusChip — five semantics, no others (§11)                        */
/* ------------------------------------------------------------------ */

export type ChipTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const CHIP_CLASS: Record<ChipTone, string> = {
  success: styles.chipSuccess ?? '',
  warning: styles.chipWarning ?? '',
  danger: styles.chipDanger ?? '',
  info: styles.chipInfo ?? '',
  neutral: styles.chipNeutral ?? '',
};

export interface StatusChipProps extends HTMLAttributes<HTMLSpanElement> {
  readonly tone: ChipTone;
}

export function StatusChip({ tone, children, className, ...rest }: StatusChipProps) {
  return (
    <span
      className={[styles.chip, CHIP_CLASS[tone], className ?? ''].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </span>
  );
}

/** Maps a business status label onto one of the five approved semantics. */
export function statusTone(status: string): ChipTone {
  switch (status.toLowerCase()) {
    case 'paid':
    case 'active':
    case 'resolved':
      return 'success';
    case 'pending':
    case 'awaiting':
      return 'warning';
    case 'overdue':
    case 'failed':
      return 'danger';
    case 'approved':
    case 'issued':
      return 'info';
    default:
      return 'neutral';
  }
}

/* ------------------------------------------------------------------ */
/* CountPill, Avatar                                                   */
/* ------------------------------------------------------------------ */

export function CountPill({ children, className, ...rest }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className={[styles.countPill, className ?? ''].filter(Boolean).join(' ')} {...rest}>
      {children}
    </span>
  );
}

export interface AvatarProps {
  readonly initials: string;
  readonly size?: 24 | 26 | 32;
  readonly tone?: 'light' | 'navy';
  /** Full name, so the avatar is not an unlabelled blob to a screen reader. */
  readonly name?: string;
}

const AVATAR_SIZE_CLASS = {
  24: styles.avatar24,
  26: styles.avatar26,
  32: styles.avatar32,
} as const;

export function Avatar({ initials, size = 24, tone = 'light', name }: AvatarProps) {
  const classes = [
    styles.avatar,
    AVATAR_SIZE_CLASS[size],
    tone === 'navy' ? styles.avatarNavy : styles.avatarLight,
  ].join(' ');

  return (
    <span className={classes} role="img" aria-label={name ?? initials}>
      <span aria-hidden="true">{initials}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Card (§8)                                                           */
/* ------------------------------------------------------------------ */

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  readonly padding?: 'standard' | 'compact' | 'flush';
  /** The 3px teal left border is reserved for AI surfaces (§8). */
  readonly ai?: boolean;
}

const CARD_PADDING_CLASS = {
  standard: styles.cardStandard ?? '',
  compact: styles.cardCompact ?? '',
  flush: styles.cardFlush ?? '',
} as const;

export function Card({
  padding = 'standard',
  ai = false,
  children,
  className,
  ...rest
}: CardProps) {
  const classes = [
    styles.card,
    CARD_PADDING_CLASS[padding],
    ai ? styles.cardAi : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  );
}

export interface CardHeaderProps {
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  readonly action?: ReactNode;
  readonly id?: string;
}

export function CardHeader({ title, subtitle, action, id }: CardHeaderProps) {
  return (
    <div className={styles.cardHeader}>
      <div>
        <h3 className={styles.cardTitle} id={id}>
          {title}
        </h3>
        {subtitle !== undefined && <p className={styles.cardSubtitle}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function CardFooter({ children, className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={[styles.cardFooter, className ?? ''].filter(Boolean).join(' ')} {...rest}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Identifier link, segmented control, spinner, skeleton               */
/* ------------------------------------------------------------------ */

export interface IdentifierLinkProps extends HTMLAttributes<HTMLAnchorElement> {
  readonly href: string;
}

export function IdentifierLink({ href, children, className, ...rest }: IdentifierLinkProps) {
  return (
    <a
      href={href}
      className={[styles.identifier, className ?? ''].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </a>
  );
}

export interface SegmentedControlProps<T extends string> {
  readonly label: string;
  readonly options: ReadonlyArray<{
    readonly value: T;
    readonly label: string;
    readonly count?: number;
  }>;
  readonly value: T;
  readonly onChange: (value: T) => void;
}

/**
 * Implemented as a real tablist so arrow keys work and the active option is
 * announced — the approved prototype used unlabelled divs (R-08).
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: SegmentedControlProps<T>) {
  const activeIndex = options.findIndex((option) => option.value === value);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (delta === 0) return;
    event.preventDefault();
    const next = options[(activeIndex + delta + options.length) % options.length];
    if (next !== undefined) onChange(next.value);
  };

  return (
    <div className={styles.segmented} role="tablist" aria-label={label} onKeyDown={onKeyDown}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={[styles.segment, selected ? styles.segmentActive : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onChange(option.value)}
          >
            {option.label}
            {option.count !== undefined && <CountPill>{option.count}</CountPill>}
          </button>
        );
      })}
    </div>
  );
}

export function Spinner({ size = 16, label }: { readonly size?: number; readonly label?: string }) {
  return (
    <span
      className={styles.spinner}
      style={{ width: size, height: size }}
      role={label === undefined ? 'presentation' : 'status'}
      aria-label={label}
    />
  );
}

export function Skeleton({
  width = '100%',
  height = 12,
  radius,
}: {
  readonly width?: number | string;
  readonly height?: number | string;
  readonly radius?: number;
}) {
  return (
    <span
      className={styles.skeleton}
      aria-hidden="true"
      style={{ width, height, ...(radius === undefined ? {} : { borderRadius: radius }) }}
    />
  );
}

export function VisuallyHidden({ children }: { readonly children: ReactNode }) {
  return <span className={styles.srOnly}>{children}</span>;
}
